import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';

export type TemplateInsertAction = 'insert' | 'cancel';

interface TemplateInsertPromptProps {
  open: boolean;
  templateName: string | null;
  onResolve: (action: TemplateInsertAction) => void;
}

const SIGNUS_BLUE = '#1014f5';
const SIGNUS_BLUE_HOVER = '#2a2eff';
const SIGNUS_BLUE_PRESSED = '#0a0dcc';

const useStyles = makeStyles({
  content: {
    fontSize: '14px',
  },
  // Force Cancel + primary side-by-side on the right, no vertical stacking
  // even in the narrow Word task pane (Fluent's default DialogActions stacks
  // on small widths).
  actions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    gap: '8px',
    '& > button': {
      width: 'auto',
      flex: '0 0 auto',
    },
  },
  button: {
    minWidth: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    '& *': { whiteSpace: 'nowrap' },
  },
  secondaryText: {
    color: SIGNUS_BLUE,
    ':hover': { color: SIGNUS_BLUE_HOVER },
    ':hover:active': { color: SIGNUS_BLUE_PRESSED },
  },
  primary: {
    backgroundColor: SIGNUS_BLUE,
    border: `1px solid ${SIGNUS_BLUE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: SIGNUS_BLUE_HOVER,
      border: `1px solid ${SIGNUS_BLUE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: SIGNUS_BLUE_PRESSED,
      border: `1px solid ${SIGNUS_BLUE_PRESSED}`,
      color: '#ffffff',
    },
  },
});

const TemplateInsertPrompt: React.FC<TemplateInsertPromptProps> = ({
  open,
  templateName,
  onResolve,
}) => {
  const styles = useStyles();
  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onResolve('cancel');
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Use template{templateName ? `: ${templateName}` : ''}</DialogTitle>
          <DialogContent className={styles.content}>
            This will replace the current document contents with the template. This action cannot be
            undone.
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button
              className={mergeClasses(styles.button, styles.secondaryText)}
              onClick={() => onResolve('cancel')}
            >
              Cancel
            </Button>
            <Button
              className={mergeClasses(styles.button, styles.primary)}
              appearance="primary"
              onClick={() => onResolve('insert')}
            >
              Use template
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TemplateInsertPrompt;
